import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/AdminNotPageFormCreator.css';
import ButtonRegular from './ui/ButtonRegular';
import ButtonDelete from './ui/ButtonDelete';
import formatDelivery, { getDeliveryPrice, DELIVERY_OPTIONS } from '../utils/formatDelivery'; 

function AdminNotPageFormCreator() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rowSavingIndex, setRowSavingIndex] = useState(null);
    const [error, setError] = useState('');
    
    const [products, setProducts] = useState([]);
    const [newProduct, setNewProduct] = useState({ name: '', price: '' });

    const [form, setForm] = useState({
        title: '',
        delivery: {
            method: '',
            label: '',
            price: 0
        }
    });

    const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    const fetchFormProducts = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, { headers: authHeader() });
            const data = resp.data || {};
            
            setProducts(Array.isArray(data.products) ? data.products : []);
            
            const deliveryMethod = data.deliveryMethod || '';
            setForm({
                title: data.title || '',
                delivery: {
                    method: deliveryMethod,
                    label: formatDelivery(deliveryMethod),
                    price: getDeliveryPrice(deliveryMethod)
                }
            });
            setError('');
        } catch (err) {
            console.error(err);
            setError('Błąd podczas pobierania danych formularza');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchFormProducts();
    }, [fetchFormProducts]);

    const handleChange = e => {
        const { name, value } = e.target;

        if (name === 'delivery') {
            const updatedDelivery = {
                method: value,
                label: formatDelivery(value),
                price: getDeliveryPrice(value),
            };

            setForm(prev => ({
                ...prev,
                delivery: updatedDelivery,
            }));

            saveDeliveryMethod(updatedDelivery.method);
            return;
        }

        setForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const persistProducts = async (updatedProducts, currentMethod = form.delivery.method) => {
        const url = `${process.env.REACT_APP_API_URL}/api/forms/answear/${id}`;
        return axios.put(
            url, 
            { products: updatedProducts, deliveryMethod: currentMethod }, 
            { headers: authHeader() }
        );
    };

    const saveDeliveryMethod = async (method) => {
        setSaving(true);
        try {
            await persistProducts(products, method);
            setError('');
        } catch (err) {
            console.error('Błąd przy zapisie metody dostawy', err);
            setError('Nie udało się zapisać metody dostawy');
        } finally {
            setSaving(false);
        }
    };

    const handleAddProduct = async () => {
        setError('');
        const name = (newProduct.name || '').trim();
        const priceNum = parseFloat(newProduct.price);
        
        if (!name || isNaN(priceNum) || priceNum < 0) {
            setError('Podaj poprawną nazwę i cenę produktu.');
            return;
        }

        const updated = [...products, { name, price: priceNum }];
        setSaving(true);
        try {
            await persistProducts(updated);
            setProducts(updated);
            setNewProduct({ name: '', price: '' });
        } catch (err) {
            console.error(err);
            setError('Nie udało się dodać produktu');
        } finally {
            setSaving(false);
        }
    };

    const handleEditProduct = async (index) => {
        setError('');
        const prod = products[index];
        const name = (prod.name || '').trim();
        const priceNum = parseFloat(prod.price);

        if (!name || isNaN(priceNum) || priceNum < 0) {
            setError('Nazwa nie może być pusta, a cena musi być >= 0.');
            return;
        }

        const updated = [...products];
        updated[index] = { name, price: priceNum };

        setRowSavingIndex(index);
        try {
            await persistProducts(updated);
            setProducts(updated);
        } catch (err) {
            console.error(err);
            setError('Nie udało się zapisać zmian produktu');
        } finally {
            setRowSavingIndex(null);
        }
    };

    const handleDeleteProduct = async (index) => {
        if (!window.confirm('Czy na pewno chcesz usunąć ten produkt?')) return;
        setError('');
        const updated = products.filter((_, i) => i !== index);
        setSaving(true);
        try {
            await persistProducts(updated);
            setProducts(updated);
        } catch (err) {
            console.error(err);
            setError('Nie udało się usunąć produktu');
        } finally {
            setSaving(false);
        }
    };

    const deleteForm = async () => {
        if (!window.confirm('Czy na pewno chcesz usunąć ten formularz?')) return;
        setSaving(true);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, { headers: authHeader() });
            navigate('/profile');
        } catch (err) {
            console.error(err);
            setError('Nie udało się usunąć formularza');
        } finally {
            setSaving(false);
        }
    };

    const updateProductField = (index, field, value) => {
        const updated = [...products];
        updated[index] = { ...updated[index], [field]: value };
        setProducts(updated);
    };

    return (
        <div className="form-container">
            <div className="form-content admin-card">
                <h2 className="form-title" style={{ color: 'var(--accent-dark)' }}>
                    {form.title || 'Produkty w formularzu'}
                </h2>

                {loading ? (
                    <p>Ładowanie...</p>
                ) : (
                    <>
                        {error && <p className="error-text">{error}</p>}

                        {/* Sekcja Dostawy */}
                        <div className="form-section">
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: 12 }}>
                                    <strong>Wybierz opcję dostawy:</strong>
                                </label>
                                <select
                                    name="delivery"
                                    value={form.delivery.method}
                                    onChange={handleChange}
                                    disabled={saving}
                                    required
                                    className="form-input"
                                    style={{ maxWidth: 360 }}
                                >
                                    <option value="">--Wybierz opcję--</option>
                                    {DELIVERY_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label} – {option.price.toFixed(2)} PLN
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Lista produktów */}
                        <div className="form-section">
                            <h3>Lista produktów</h3>
                            <table className="admin-table products-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}>#</th>
                                        <th>Nazwa</th>
                                        <th style={{ width: 140 }}>Cena</th>
                                        <th style={{ width: 190 }}>Akcje</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.length === 0 && (
                                        <tr>
                                            <td colSpan="4">Brak produktów w formularzu.</td>
                                        </tr>
                                    )}
                                    {products.map((prod, idx) => (
                                        <tr key={idx} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <input
                                                    className="table-input"
                                                    value={prod.name ?? ''}
                                                    onChange={(e) => updateProductField(idx, 'name', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    className="table-input"
                                                    type="number"
                                                    step="0.01"
                                                    /* POPRAWIONE: Usunięto .toFixed(2), który crashował input */
                                                    value={prod.price ?? ''} 
                                                    onChange={(e) => updateProductField(idx, 'price', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <ButtonRegular 
                                                    className="small" 
                                                    onClick={() => handleEditProduct(idx)} 
                                                    disabled={rowSavingIndex === idx || saving}
                                                >
                                                    {rowSavingIndex === idx ? 'Zapisywanie...' : '💾 Zapisz'}
                                                </ButtonRegular>
                                                <ButtonDelete 
                                                    className="small" 
                                                    onClick={() => handleDeleteProduct(idx)} 
                                                    disabled={saving}
                                                >
                                                    🗑️ Usuń
                                                </ButtonDelete>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Dodaj nowy produkt */}
                        <div className="form-section">
                            <h3>Dodaj nowy produkt</h3>
                            <div className="add-product-row">
                                <input
                                    placeholder="Nazwa"
                                    value={newProduct.name}
                                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                                />
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Cena"
                                    /* POPRAWIONE: Bezpieczne wiązanie wartości bez .toFixed(2) */
                                    value={newProduct.price}
                                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                                />
                                <ButtonRegular onClick={handleAddProduct} disabled={saving}>
                                    {saving ? 'Proszę czekać...' : '➕ Dodaj produkt'}
                                </ButtonRegular>
                            </div>
                        </div>

                        <div className="action-buttons" style={{ marginTop: 8 }}>
                            <ButtonDelete onClick={deleteForm} disabled={saving}>🗑️ Usuń cały formularz</ButtonDelete>
                            <ButtonRegular onClick={() => navigate('/profile')} disabled={saving}>⬅️ Powrót</ButtonRegular>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminNotPageFormCreator;